import { motion } from "framer-motion";
import { ArrowRight, Phone, Scale, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const heroBrandImage = `${process.env.PUBLIC_URL}/assets/bufete-hero.jpg`;

const navItems = [
  { label: "Despacho", id: "despacho" },
  { label: "Áreas", id: "areas" },
  { label: "Confianza", id: "confianza" },
  { label: "Consulta", id: "consulta" },
];

const serviceAreas = [
  {
    number: "01",
    title: "Bancario y reclamaciones",
    description: "Casos complejos, presión negociadora y una defensa precisa frente a entidades y deudores.",
  },
  {
    number: "02",
    title: "Vivienda y patrimonio",
    description: "Arrendamientos, desahucios, propiedad y protección jurídica de activos con enfoque ejecutivo.",
  },
  {
    number: "03",
    title: "Familia y sucesiones",
    description: "Procesos sensibles tratados con criterio, firmeza y una comunicación muy clara.",
  },
  {
    number: "04",
    title: "Penal, laboral y mercantil",
    description: "Una estructura flexible para acompañar litigios y decisiones clave de particulares y empresas.",
  },
];

const impactPoints = [
  { value: "+15 años", label: "de experiencia" },
  { value: "Imagen premium", label: "desde el primer scroll" },
  { value: "Consulta clara", label: "sin fricción ni ruido" },
];

const trustPoints = [
  "Más jerarquía visual y mejor percepción de marca.",
  "Menos texto, más intención comercial y autoridad.",
  "Una navegación más directa para llevar al contacto.",
];

const testimonials = [
  {
    quote: "Me sentí acompañada, entendida y muy bien orientada desde el inicio.",
    author: "Angela · Cliente particular",
  },
  {
    quote: "Rapidez, criterio y una sensación de despacho sólido y actual.",
    author: "Marcos · Empresa cliente",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const scrollToId = (id) => {
  const element = document.getElementById(id);
  const header = document.querySelector('[data-testid="site-header"]');
  if (!element) return;
  const headerOffset = header?.offsetHeight || 0;
  const top = element.getBoundingClientRect().top + window.scrollY - headerOffset - 18;
  window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
};

const ServiceRow = ({ item }) => (
  <motion.button
    variants={fadeUp}
    type="button"
    onClick={() => scrollToId("consulta")}
    className="group grid w-full gap-4 border-b border-[#272120]/10 py-6 text-left transition-colors duration-300 hover:border-[#A33C35] md:grid-cols-[80px_minmax(0,1fr)_34px] md:items-center"
    data-testid={`service-row-${item.number}`}
  >
    <span className="text-[11px] uppercase tracking-[0.28em] text-[#A33C35]">{item.number}</span>
    <div>
      <h3 className="text-2xl font-light tracking-tight text-[#272120] md:text-3xl">{item.title}</h3>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#272120]/68 md:text-base">{item.description}</p>
    </div>
    <ArrowRight className="h-5 w-5 text-[#272120] transition-transform duration-300 group-hover:translate-x-2 group-hover:text-[#A33C35]" />
  </motion.button>
);

const BufeteLogo = ({ light = false, compact = false, testId = "bufete-logo" }) => {
  const textColor = light ? "text-white" : "text-[#2C2A2D]";
  const subColor = light ? "text-white/82" : "text-[#474449]";
  const lineColor = light ? "bg-[#D7C3A3]/55" : "bg-[#C8B18C]";
  const topSize = compact ? "text-[1.25rem] sm:text-[1.55rem]" : "text-[1.7rem] sm:text-[2.35rem]";
  const bottomSize = compact ? "text-[0.82rem] sm:text-[1rem]" : "text-[1rem] sm:text-[1.35rem]";

  return (
    <div className="inline-flex flex-col leading-none" data-testid={testId}>
      <div className={`flex items-end gap-2 font-serif font-light ${topSize} ${textColor}`}>
        <span>Bufete Costa</span>
        <span className="pb-[0.05em] text-[#B63A32]">&amp;</span>
        <span>Asociados</span>
      </div>
      <div className={`mt-1 h-px w-full ${lineColor}`} />
      <div className={`mt-1 text-center font-serif font-light uppercase tracking-[0.15em] ${bottomSize} ${subColor}`}>
        Abogados
      </div>
    </div>
  );
};

export default function LegalLandingPage() {
  return (
    <div className="App overflow-x-hidden bg-[#FAF6F0] text-[#272120]" data-testid="legal-landing-page">
      <header className="sticky top-0 z-50 border-b border-[#272120]/10 bg-[rgba(250,246,240,0.94)] backdrop-blur-xl" data-testid="site-header">
        <div className="container-shell py-4 md:py-5">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <button type="button" onClick={() => scrollToId("inicio")} className="flex flex-col items-center text-center" data-testid="brand-home-link">
              <span className="mb-1 text-[10px] uppercase tracking-[0.34em] text-[#A33C35]">Propuesta LOUD DIVISION</span>
              <BufeteLogo compact testId="header-logo" />
            </button>

            <div className="hidden h-px w-full max-w-[620px] bg-[linear-gradient(90deg,transparent,rgba(39,33,32,0.12),transparent)] lg:block" data-testid="header-divider" />

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
              <nav className="hidden items-center justify-center gap-7 md:flex" data-testid="desktop-navigation">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToId(item.id)}
                    className="text-sm tracking-[0.08em] text-[#272120]/72 transition-colors duration-300 hover:text-[#A33C35]"
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <Button
                type="button"
                onClick={() => scrollToId("areas")}
                variant="outline"
                className="hidden h-11 rounded-none border border-[#272120]/15 bg-transparent px-4 text-xs uppercase tracking-[0.18em] text-[#272120] hover:border-[#A33C35] hover:bg-[#A33C35]/6 sm:inline-flex md:hidden"
                data-testid="mobile-areas-button"
              >
                Áreas
              </Button>

              <Button
                type="button"
                onClick={() => scrollToId("consulta")}
                className="h-11 rounded-none border border-[#8F342F] bg-[#8F342F] px-5 text-xs uppercase tracking-[0.22em] text-[#FAF6F0] transition-colors duration-300 hover:bg-[#742B27] md:px-7"
                data-testid="header-consultation-button"
              >
                Solicitar consulta
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main id="inicio" data-testid="page-main">
        <section className="relative overflow-hidden border-b border-[#272120]/10" data-testid="hero-section">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(163,60,53,0.1),transparent_24%),radial-gradient(circle_at_78%_18%,rgba(122,95,72,0.08),transparent_22%),linear-gradient(180deg,rgba(255,248,242,0.75),rgba(250,246,240,1))]" />
          <div className="absolute left-[7%] top-0 hidden h-full w-px bg-[linear-gradient(180deg,transparent,rgba(39,33,32,0.08),transparent)] xl:block" />
          <div className="absolute right-[8%] top-0 hidden h-full w-px bg-[linear-gradient(180deg,transparent,rgba(39,33,32,0.06),transparent)] xl:block" />
          <div className="container-shell relative grid min-h-[calc(100vh-112px)] items-center gap-8 py-10 md:grid-cols-12 md:py-12">
            <motion.div initial="hidden" animate="show" variants={stagger} className="md:col-span-7 lg:pr-6">
              <motion.p variants={fadeUp} className="eyebrow" data-testid="hero-eyebrow">
                Despacho multidisciplinar · Barcelona · edición editorial
              </motion.p>
              <motion.div variants={fadeUp} className="mt-4" data-testid="hero-logo-lockup">
                <BufeteLogo testId="hero-logo" />
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="balanced-text mt-5 max-w-4xl text-5xl font-light leading-[0.88] tracking-[-0.05em] text-[#272120] sm:text-6xl lg:text-[6.35rem]"
                data-testid="hero-heading"
              >
                Justicia,
                <br />
                presencia y poder visual.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mt-5 max-w-[42rem] text-base leading-relaxed text-[#272120]/70 sm:text-lg"
                data-testid="hero-subtitle"
              >
                Un rediseño con una estética más refinada, más cálida y mejor alineada con la identidad actual del bufete.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-6 max-w-[35rem] border-l border-[#A33C35]/40 pl-5"
                data-testid="hero-editorial-quote"
              >
                <p className="text-xl font-light italic leading-relaxed text-[#4A3B38] sm:text-2xl">
                  “Una presencia jurídica de alto nivel empieza por una primera impresión impecable.”
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-9 flex flex-col gap-4 sm:flex-row">
                <Button
                  type="button"
                  onClick={() => scrollToId("consulta")}
                  className="group h-14 rounded-none border border-[#8F342F] bg-[#8F342F] px-8 text-sm uppercase tracking-[0.18em] text-[#FAF6F0] transition-colors duration-300 hover:bg-[#742B27]"
                  data-testid="hero-primary-cta"
                >
                  Pedir valoración del caso
                  <ArrowRight className="ml-3 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => scrollToId("areas")}
                  className="h-14 rounded-none border border-[#272120]/15 bg-transparent px-8 text-sm uppercase tracking-[0.18em] text-[#272120] hover:border-[#A33C35] hover:bg-[#A33C35]/6"
                  data-testid="hero-secondary-cta"
                >
                  Ver áreas clave
                </Button>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8 grid gap-4 sm:grid-cols-3">
                {impactPoints.map((item) => (
                  <div key={item.value} className="border border-[#272120]/10 bg-[rgba(255,250,245,0.74)] px-4 py-4" data-testid={`hero-proof-${item.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="text-lg text-[#272120] md:text-xl">{item.value}</p>
                    <p className="mt-2 text-sm text-[#272120]/58">{item.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="relative md:col-span-5"
            >
              <div className="relative ml-auto w-full max-w-[560px]">
                <div className="absolute -left-5 top-10 hidden h-[70%] w-[78%] border border-[#B79E87]/35 md:block" />
                <div className="relative aspect-[4/5] overflow-hidden border border-[#272120]/10 bg-[#EDE2D6]" data-testid="hero-image-frame">
                  <img
                    src={heroBrandImage}
                    alt="Imagen corporativa del bufete"
                    className="h-full w-full object-cover object-center"
                    style={{ filter: "saturate(0.72) sepia(0.12) contrast(0.96) brightness(0.95)" }}
                    data-testid="hero-image"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(39,33,32,0.04)_20%,rgba(39,33,32,0.74)_100%)]" />
                  <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6 text-white" data-testid="hero-image-caption">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.28em] text-white/70">Nueva percepción</p>
                      <p className="mt-2 text-xl font-light">Más elegancia, más coherencia de marca</p>
                    </div>
                    <Scale className="h-8 w-8 text-[#D2B9A3]" />
                  </div>
                </div>
                <div className="absolute -bottom-4 left-0 z-10 max-w-[290px] border border-[#272120]/10 bg-[#FAF6F0] p-5 shadow-[0_10px_30px_rgba(39,33,32,0.08)] md:-left-10" data-testid="hero-floating-card">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#A33C35]">Consulta estratégica</p>
                  <p className="mt-3 text-lg font-light">Reunión presencial o virtual con enfoque inmediato en el caso.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="despacho" className="container-shell scroll-mt-28 py-16 md:py-18" data-testid="about-section">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid gap-8 md:grid-cols-12 md:items-start">
            <motion.div variants={fadeUp} className="md:col-span-7">
              <p className="eyebrow" data-testid="about-eyebrow">Despacho y propuesta</p>
              <h2 className="balanced-text mt-4 text-4xl font-light leading-[0.98] tracking-[-0.04em] sm:text-5xl lg:text-6xl" data-testid="about-heading">
                Menos web de bufete clásica. Más dirección, contraste y autoridad.
              </h2>
            </motion.div>

            <motion.div variants={fadeUp} className="md:col-span-5">
              <p className="text-lg leading-relaxed text-[#272120]/72 sm:text-xl" data-testid="about-description-1">
                Se compacta el contenido y se refuerza la marca con una estética editorial más decidida, sin perder credibilidad jurídica.
              </p>
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="mt-10 grid gap-5 md:grid-cols-12">
            <motion.div variants={fadeUp} className="overflow-hidden border border-[#272120]/10 bg-[#F1E7DC] md:col-span-5" data-testid="about-image-wrapper">
              <img
                src="https://images.unsplash.com/photo-1665224752561-85f4da9a5658?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Profesional del ámbito legal"
                className="aspect-[4/5] h-full w-full object-cover object-center"
                data-testid="about-image"
              />
            </motion.div>

            <motion.div variants={fadeUp} className="grid gap-4 md:col-span-7">
              <div className="border border-[#272120]/10 bg-[rgba(255,250,245,0.86)] p-7" data-testid="about-highlight-card">
                <ShieldCheck className="h-8 w-8 text-[#A33C35]" />
                <p className="mt-6 text-2xl font-light leading-tight">
                  Un posicionamiento visual pensado para vender confianza antes de vender servicios.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {trustPoints.map((item, index) => (
                  <div key={item} className="border border-[#272120]/10 bg-[rgba(255,250,245,0.78)] px-4 py-4" data-testid={`value-item-${index + 1}`}>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#A33C35]">0{index + 1}</p>
                    <p className="mt-3 text-sm leading-relaxed text-[#272120]/72">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </section>

        <section id="areas" className="container-shell scroll-mt-28 py-16 md:py-18" data-testid="services-section">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid gap-6 md:grid-cols-12 md:items-end">
            <motion.div variants={fadeUp} className="md:col-span-8">
              <p className="eyebrow" data-testid="services-eyebrow">Áreas clave</p>
              <h2 className="balanced-text mt-4 text-4xl font-light leading-tight tracking-[-0.04em] sm:text-5xl" data-testid="services-heading">
                Una estructura más corta, más tensa y mucho más memorable.
              </h2>
            </motion.div>
            <motion.p variants={fadeUp} className="text-base leading-relaxed text-[#272120]/70 md:col-span-4 sm:text-lg" data-testid="services-description">
              Se priorizan cuatro bloques con mejor lectura y más intención comercial.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} variants={stagger} className="mt-8 border-t border-[#272120]/10">
            {serviceAreas.map((item) => (
              <ServiceRow key={item.number} item={item} />
            ))}
          </motion.div>
        </section>

        <section className="relative overflow-hidden bg-[#1D1716] py-16 text-white md:py-18" data-testid="differential-section">
          <div className="absolute inset-0 opacity-[0.08]" data-testid="differential-bg-word">
            <div className="flex h-full items-center justify-center text-center text-[18vw] font-light tracking-[0.25em] text-white">
              CASOS
            </div>
          </div>
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Acuerdo profesional"
              className="h-full w-full object-cover object-center"
              data-testid="differential-background-image"
            />
          </div>
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(29,23,22,0.95)_10%,rgba(55,34,31,0.76)_52%,rgba(29,23,22,0.95)_100%)]" />

          <div className="container-shell relative grid gap-8 md:grid-cols-12 md:items-end">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="md:col-span-7">
              <motion.p variants={fadeUp} className="eyebrow text-[#D2B9A3]" data-testid="differential-eyebrow">
                Bloque diferencial
              </motion.p>
              <motion.h2 variants={fadeUp} className="mt-4 max-w-4xl text-4xl font-light leading-[0.98] tracking-[-0.04em] sm:text-5xl lg:text-6xl" data-testid="differential-heading">
                Una web legal puede ser sobria sin parecer antigua. Y premium sin dejar de inspirar seguridad.
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid gap-4 md:col-span-5">
              {impactPoints.map((item) => (
                <motion.div key={item.value} variants={fadeUp} className="border border-white/12 bg-[rgba(255,250,245,0.05)] px-5 py-4" data-testid={`proof-item-${item.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-3xl font-light md:text-4xl">{item.value}</p>
                  <p className="mt-2 text-sm text-white/70 md:text-base">{item.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="confianza" className="container-shell scroll-mt-28 py-16 md:py-18" data-testid="testimonials-section">
          <div className="grid gap-5 lg:grid-cols-[1.28fr_0.72fr]">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="border border-[#272120]/10 bg-[rgba(255,250,245,0.84)] p-7 md:p-8">
              <motion.p variants={fadeUp} className="eyebrow" data-testid="testimonials-eyebrow">
                Confianza real
              </motion.p>
              <motion.div variants={fadeUp} className="mt-4 flex gap-1 text-[#A33C35]" data-testid="testimonial-stars-main">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star key={`star-main-${index}`} className="h-4 w-4 fill-current" />
                ))}
              </motion.div>
              <motion.h2 variants={fadeUp} className="mt-6 text-3xl font-light italic leading-relaxed tracking-[-0.03em] sm:text-4xl" data-testid="testimonials-heading">
                “Me transmitieron profesionalidad, claridad y una sensación de control absoluto del proceso.”
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-8 text-sm uppercase tracking-[0.18em] text-[#272120]/56" data-testid="testimonial-author-main">
                Angela · Cliente particular
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid gap-4">
              {testimonials.map((item, index) => (
                <motion.div key={item.author} variants={fadeUp} className="border border-[#272120]/10 bg-[#FAF6F0] p-5" data-testid={`testimonial-card-${index + 1}`}>
                  <p className="text-lg font-light leading-relaxed text-[#272120]" data-testid={`testimonial-quote-${index + 1}`}>
                    “{item.quote}”
                  </p>
                  <p className="mt-5 text-xs uppercase tracking-[0.2em] text-[#272120]/55" data-testid={`testimonial-author-${index + 1}`}>
                    {item.author}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="consulta" className="border-t border-[#272120]/10 bg-[rgba(255,250,245,0.86)] py-16 md:py-18" data-testid="cta-section">
          <div className="container-shell grid gap-7 md:grid-cols-12 md:items-start">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="md:col-span-7">
              <motion.p variants={fadeUp} className="eyebrow" data-testid="cta-eyebrow">
                Cierre comercial
              </motion.p>
              <motion.h2 variants={fadeUp} className="max-w-4xl pt-4 text-4xl font-light leading-[0.98] tracking-[-0.04em] sm:text-5xl lg:text-6xl" data-testid="cta-heading">
                Pida una valoración del caso y convierta la primera impresión en decisión.
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid gap-4 border border-[#272120]/10 bg-[#FAF6F0] p-7 md:col-span-5" data-testid="cta-contact-panel">
              <motion.div variants={fadeUp} className="border-b border-[#272120]/10 pb-5">
                <p className="text-sm uppercase tracking-[0.24em] text-[#A33C35]" data-testid="cta-panel-label">Consulta estratégica</p>
                <p className="mt-3 text-2xl font-light" data-testid="cta-panel-title">Solicitar consulta</p>
              </motion.div>

              <motion.div variants={fadeUp} className="space-y-3" data-testid="cta-contact-details">
                <p className="text-base leading-relaxed text-[#272120]/72" data-testid="cta-description">
                  Atención personalizada, lectura inicial del caso y siguiente paso claro.
                </p>
                <div className="flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-[#272120]/62" data-testid="cta-meeting-format">
                  <Phone className="h-4 w-4 text-[#A33C35]" />
                  Reunión presencial o virtual
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-col gap-3">
                <Button type="button" className="group h-14 w-full justify-between whitespace-normal rounded-none border border-[#8F342F] bg-[#8F342F] px-6 text-left text-sm uppercase leading-tight tracking-[0.16em] text-[#FAF6F0] transition-colors duration-300 hover:bg-[#742B27]" data-testid="cta-primary-button">
                  Pedir valoración del caso
                  <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => scrollToId("inicio")}
                  className="h-14 w-full justify-start whitespace-normal rounded-none border border-[#272120]/15 bg-transparent px-6 text-left text-sm uppercase leading-tight tracking-[0.16em] text-[#272120] hover:border-[#A33C35] hover:bg-[#A33C35]/6"
                  data-testid="cta-secondary-button"
                >
                  Volver arriba
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="bg-[#1D1716] py-9 text-white" data-testid="site-footer">
        <div className="container-shell grid gap-6 md:grid-cols-12 md:items-end">
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/45" data-testid="footer-label">Mockup comercial</p>
            <div className="mt-3">
              <BufeteLogo light compact testId="footer-logo" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-white/62 md:col-span-4" data-testid="footer-links-block">
            <button type="button" onClick={() => scrollToId("despacho")} data-testid="footer-link-despacho">Despacho</button>
            <button type="button" onClick={() => scrollToId("areas")} data-testid="footer-link-areas">Áreas</button>
            <button type="button" onClick={() => scrollToId("consulta")} data-testid="footer-link-consulta">Consulta</button>
          </div>
          <div className="md:col-span-3 md:text-right">
            <p className="text-sm uppercase tracking-[0.18em] text-white/45" data-testid="footer-credit">© 2026 Loud Division Mockup</p>
          </div>
        </div>
      </footer>
    </div>
  );
}